import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, FileDown, Eye, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DETAILS_FIELDS = [
    ['situation_depart', 'Situation de départ'],
    ['cible', 'Cible / objectifs'],
    ['sponsors', 'Sponsors & gouvernance'],
    ['livrables', 'Livrables attendus'],
    ['contraintes', 'Contraintes & risques'],
];

export default function ExportProjectPreview({ projectId, projectName, trigger }) {
    const { session } = useAuth();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [data, setData] = useState(null);
    const [titleOverride, setTitleOverride] = useState('');
    const [eyebrowOverride, setEyebrowOverride] = useState('');

    const authHeader = useCallback(() => ({
        headers: { Authorization: `Bearer ${session?.access_token}` },
    }), [session]);

    const loadPreview = useCallback(async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/exports/project/${projectId}/preview-data`, authHeader());
            setData(r.data);
            setTitleOverride('');
            setEyebrowOverride('');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Chargement échoué');
            setOpen(false);
        } finally {
            setLoading(false);
        }
    }, [projectId, authHeader]);

    useEffect(() => {
        if (open) loadPreview();
    }, [open, loadPreview]);

    const updateProjectField = (field, value) => {
        setData(d => ({ ...d, project: { ...d.project, [field]: value } }));
    };
    const updateDetailField = (key, value) => {
        setData(d => ({ ...d, details: { ...d.details, [key]: value } }));
    };
    const updateActor = (idx, field, value) => {
        setData(d => ({
            ...d,
            actors: d.actors.map((a, i) => i === idx ? { ...a, [field]: value } : a),
        }));
    };
    const removeActor = (idx) => {
        setData(d => ({ ...d, actors: d.actors.filter((_, i) => i !== idx) }));
    };

    const generatePdf = async () => {
        if (!data) return;
        setGenerating(true);
        try {
            const payload = {
                project: data.project,
                kpis: data.kpis,
                actors: data.actors,
                details: data.details,
                tasks: data.tasks,
                title_override: titleOverride || null,
                eyebrow_override: eyebrowOverride || null,
            };
            const resp = await fetch(`${API}/exports/project/${projectId}.pdf`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${session?.access_token}`,
                },
                body: JSON.stringify(payload),
            });
            if (!resp.ok) {
                const t = await resp.text();
                throw new Error(t.slice(0, 200));
            }
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `projet_${(data.project.name || 'export').replace(/[^\w-]/g, '_')}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success('PDF projet généré');
            setOpen(false);
        } catch (e) {
            toast.error(`Génération échouée : ${e.message || e}`);
        } finally {
            setGenerating(false);
        }
    };

    return (
        <>
            {trigger ? React.cloneElement(trigger, { onClick: () => setOpen(true) }) : (
                <Button variant="outline" onClick={() => setOpen(true)} data-testid="export-project-preview-btn">
                    <Eye size={14} className="mr-2" /> Prévisualiser & exporter
                </Button>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl">
                            Prévisualisation export · {projectName}
                        </DialogTitle>
                        <p className="text-sm text-muted-foreground mt-1">
                            Tous les champs sont modifiables avant génération. Le PDF reprendra exactement ces valeurs.
                        </p>
                    </DialogHeader>

                    {loading || !data ? (
                        <div className="py-12 text-center text-muted-foreground">
                            <Loader2 className="inline animate-spin mr-2" size={14} /> Chargement…
                        </div>
                    ) : (
                        <div className="space-y-6 py-2" data-testid="export-preview-body">
                            {/* Branding overrides */}
                            <section className="space-y-3 p-4 rounded-lg bg-surface-muted/60 border border-border">
                                <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">En-tête du document</div>
                                <div className="grid sm:grid-cols-2 gap-3">
                                    <div className="space-y-1.5">
                                        <Label className="text-xs">Eyebrow (au-dessus du titre)</Label>
                                        <Input
                                            value={eyebrowOverride}
                                            onChange={(e) => setEyebrowOverride(e.target.value)}
                                            placeholder={data.project.industry ? `${data.project.industry.toUpperCase()} · ${(data.project.country || '').toUpperCase()}` : 'AFRICAN VALLEY · PILOTAGE PROJET'}
                                            data-testid="export-eyebrow-input"
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label className="text-xs">Titre personnalisé</Label>
                                        <Input
                                            value={titleOverride}
                                            onChange={(e) => setTitleOverride(e.target.value)}
                                            placeholder={`Dossier projet · ${data.project.name}`}
                                            data-testid="export-title-input"
                                        />
                                    </div>
                                </div>
                            </section>

                            {/* Description */}
                            <section className="space-y-2">
                                <Label className="text-sm font-medium">Description courte</Label>
                                <Textarea
                                    rows={3}
                                    value={data.project.description || ''}
                                    onChange={(e) => updateProjectField('description', e.target.value)}
                                    data-testid="export-description"
                                />
                            </section>

                            {/* Detail sections */}
                            <section className="space-y-4">
                                <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Détail du projet</div>
                                {DETAILS_FIELDS.map(([k, lbl]) => (
                                    <div key={k} className="space-y-1.5">
                                        <Label className="text-sm">{lbl}</Label>
                                        <Textarea
                                            rows={3}
                                            value={data.details?.[k] || ''}
                                            onChange={(e) => updateDetailField(k, e.target.value)}
                                            placeholder="—"
                                            data-testid={`export-details-${k}`}
                                        />
                                    </div>
                                ))}
                            </section>

                            {/* Actors */}
                            <section className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                                        Acteurs · {data.actors?.length || 0}
                                    </div>
                                </div>
                                {(!data.actors || data.actors.length === 0) ? (
                                    <p className="text-sm text-muted-foreground italic">Aucun acteur — sera masqué du PDF.</p>
                                ) : (
                                    <div className="space-y-2">
                                        {data.actors.map((a, idx) => (
                                            <div key={idx} className="grid grid-cols-12 gap-2 items-center p-2 rounded border border-border bg-white" data-testid={`export-actor-${idx}`}>
                                                <Input
                                                    className="col-span-3 h-8 text-xs"
                                                    placeholder="Nom complet"
                                                    value={`${a.first_name || ''} ${a.last_name || ''}`.trim()}
                                                    onChange={(e) => {
                                                        const parts = e.target.value.split(' ');
                                                        updateActor(idx, 'first_name', parts[0] || '');
                                                        updateActor(idx, 'last_name', parts.slice(1).join(' '));
                                                    }}
                                                />
                                                <Input className="col-span-3 h-8 text-xs" placeholder="Fonction" value={a.role || ''} onChange={(e) => updateActor(idx, 'role', e.target.value)} />
                                                <Input className="col-span-3 h-8 text-xs" placeholder="Organisation" value={a.organization || ''} onChange={(e) => updateActor(idx, 'organization', e.target.value)} />
                                                <Input className="col-span-2 h-8 text-xs" placeholder="Email" value={a.email || ''} onChange={(e) => updateActor(idx, 'email', e.target.value)} />
                                                <button
                                                    type="button"
                                                    onClick={() => removeActor(idx)}
                                                    className="col-span-1 h-8 w-8 mx-auto rounded text-muted-foreground hover:text-destructive hover:bg-surface-muted flex items-center justify-center"
                                                    data-testid={`export-actor-remove-${idx}`}
                                                    title="Retirer cet acteur de l'export"
                                                ><X size={12} /></button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </section>

                            <section className="text-xs text-muted-foreground p-3 bg-surface-muted/40 rounded-lg border border-border/60">
                                💡 Les modifications apportées ici ne sont appliquées qu'à <strong>cet export</strong>.
                                Pour les sauvegarder définitivement, utilisez les onglets « À propos » et « Acteurs » du projet.
                            </section>
                        </div>
                    )}

                    <DialogFooter>
                        <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
                        <Button
                            type="button"
                            onClick={generatePdf}
                            disabled={generating || loading || !data}
                            className="bg-primary hover:bg-primary-hover"
                            data-testid="export-generate-pdf"
                        >
                            {generating
                                ? <><Loader2 size={14} className="mr-2 animate-spin" /> Génération…</>
                                : <><FileDown size={14} className="mr-2" /> Générer le PDF</>}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
